import { KeyRound, ShieldCheck, Users } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { PageHeader } from '@/components/common/page-header';
import { ConfirmDialog } from '@/components/fleet/confirm-dialog';
import { ErrorState, TableSkeleton } from '@/components/fleet/states';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { useConfigSigning, useUpdateConfigSigning } from '@/hooks/use-config-signing';
import { useOperators } from '@/hooks/use-operators';
import { ApiError } from '@/lib/api-client';

/**
 * Security settings — admin-only.
 *
 * Config-signing enable toggle: flips whether the API ed25519-signs the two
 * censored-region config responses (connect + bootstrap-edges). DB-backed, so it
 * takes effect without a redeploy. Clients fail-open, so toggling is safe. Only
 * the PUBLIC keyId fingerprint is shown — the private key is never exposed.
 */
export function SecurityPage(): JSX.Element {
  const state = useConfigSigning();
  const update = useUpdateConfigSigning();
  const operators = useOperators();

  const [confirmOpen, setConfirmOpen] = useState(false);
  /** The value the operator is trying to switch TO (pending confirmation). */
  const [pendingValue, setPendingValue] = useState<boolean | null>(null);

  const saving = update.isPending;

  function requestToggle(next: boolean): void {
    setPendingValue(next);
    setConfirmOpen(true);
  }

  function handleConfirm(): void {
    if (pendingValue === null) return;

    update.mutate(pendingValue, {
      onSuccess: (result) => {
        toast.success(
          result.enabled
            ? 'Config signing enabled — connect + bootstrap responses are now signed.'
            : 'Config signing disabled — responses are no longer signed.',
        );
        setConfirmOpen(false);
        setPendingValue(null);
      },
      onError: (err) => {
        setConfirmOpen(false);
        setPendingValue(null);
        toast.error(
          err instanceof ApiError ? err.message : 'Could not update config signing. Try again.',
        );
      },
    });
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Security"
        description="Runtime security controls for the API. Changes take effect without a redeploy."
      />

      {state.isLoading && <TableSkeleton rows={3} />}

      {state.isError && (
        <ErrorState
          title="Couldn't load config-signing state."
          error={state.error}
          onRetry={() => void state.refetch()}
        />
      )}

      {state.data && (
        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-4">
            <div className="space-y-1">
              <CardTitle className="flex items-center gap-2 text-base">
                <ShieldCheck className="h-4 w-4 text-primary" aria-hidden />
                Config signing
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Ed25519-sign the <span className="font-mono">connect</span> and{' '}
                <span className="font-mono">bootstrap-edges</span> responses so clients in censored
                regions can detect in-transit tampering. Clients fail-open, so this is safe to
                toggle either way.
              </p>
            </div>
            <Switch
              checked={state.data.enabled}
              disabled={saving || !state.data.keyPresent}
              onCheckedChange={requestToggle}
              aria-label="Toggle config signing"
            />
          </CardHeader>

          <CardContent className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={state.data.enabled ? 'default' : 'outline'}>
                {state.data.enabled ? 'Enabled' : 'Disabled'}
              </Badge>

              {state.data.keyPresent ? (
                <Badge variant="outline" className="gap-1">
                  <KeyRound className="h-3 w-3" aria-hidden />
                  key {state.data.keyId ?? 'present'}
                </Badge>
              ) : (
                <Badge variant="destructive">No signing key</Badge>
              )}
            </div>

            {!state.data.keyPresent && (
              <p className="text-sm text-destructive" role="alert">
                No signing key is configured on the server
                (<span className="font-mono">CONFIG_SIGNING_PRIVATE_KEY_B64</span>). Responses will
                never be signed until a key is provisioned, so the toggle is disabled.
              </p>
            )}

            <p className="text-xs text-muted-foreground">
              <span className="font-mono">keyId</span> is the public key fingerprint (safe to share).
              The private key never leaves the server and is never shown here.
            </p>
          </CardContent>
        </Card>
      )}

      {/*
        Who holds an operator role — ADR-0063 decision 5. Read-only, and the absence of any
        control here is the decision: role changes go through `npm run grant:operator`, because
        a dashboard that can mint admins turns a stolen session into persistence. Showing the
        list is what makes an unexpected operator noticeable.
      */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Users className="h-4 w-4 text-primary" aria-hidden />
            Operators
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Accounts that can sign in here. Roles are granted with{' '}
            <span className="font-mono">npm run grant:operator</span> from the cluster —
            deliberately not from this page.
          </p>
        </CardHeader>

        <CardContent className="space-y-2">
          {operators.isLoading && <TableSkeleton rows={2} />}

          {operators.isError && (
            <ErrorState
              title="Couldn't load operators."
              error={operators.error}
              onRetry={() => void operators.refetch()}
            />
          )}

          {operators.data?.map((operator) => (
            <div
              key={operator.id}
              className="flex items-center justify-between gap-3 rounded border border-border px-3 py-2"
            >
              <span className="truncate text-sm">
                {operator.email ?? <span className="text-muted-foreground">no email</span>}
              </span>
              <Badge variant={operator.role === 'admin' ? 'default' : 'outline'}>
                {operator.role}
              </Badge>
            </div>
          ))}

          {operators.data?.length === 0 && (
            <p className="text-sm text-destructive" role="alert">
              No operator accounts exist. Nobody can sign in here, and no bug-bounty payout can be
              approved, until one is granted from the cluster.
            </p>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={(next) => {
          if (!next) {
            setConfirmOpen(false);
            setPendingValue(null);
          }
        }}
        title={pendingValue ? 'Enable config signing?' : 'Disable config signing?'}
        description={
          pendingValue
            ? 'Enabling makes the API sign connect/bootstrap responses; clients fail-open so this is safe to toggle. It takes effect without a redeploy.'
            : 'Disabling stops the API from signing connect/bootstrap responses. Clients fail-open, so this is safe. It takes effect without a redeploy.'
        }
        confirmLabel={pendingValue ? 'Enable signing' : 'Disable signing'}
        pending={saving}
        onConfirm={handleConfirm}
      />
    </div>
  );
}
